import { Data } from '@blade/std-lib'
import { OAuthCode, OAuthState } from '@root/entities/auth'
import { Network } from '@root/entities/network/model'
import { PairAddress } from '@root/entities/pair/model'
import { InviteCode } from '@root/entities/referral'
import { DelugeId } from '@root/entities/user-short'
import { BooleanUtil, environment } from '@root/shared'

export class InviteCodeJoin extends Data.TaggedClass('InviteCodeJoin')<{
  inviteCode: InviteCode
}> {}

export class Trade extends Data.TaggedClass('Trade')<{
  network: Network.Network
  pair: PairAddress
  maker?: string
}> {}

export class UserProfile extends Data.TaggedClass('UserProfile')<{
  delugeId: DelugeId
}> {}
export class Profile extends Data.TaggedClass('Profile')<{}> {}
export class Nft extends Data.TaggedClass('Nft')<{}> {}
export class Homepage extends Data.TaggedClass('Homepage')<{}> {}
export class TelegramAuth extends Data.TaggedClass('TelegramAuth')<{
  code: OAuthCode
  action: string
  isExists: boolean
}> {}
export class GoogleAuth extends Data.TaggedClass('GoogleAuth')<{
  state: OAuthState | undefined
  code: OAuthCode | undefined
}> {}
export class TwitterAuth extends Data.TaggedClass('TwitterAuth')<{
  authCode: OAuthCode | undefined
  state: OAuthState | undefined
}> {}
export class CheckCurrency extends Data.TaggedClass('CheckCurrency')<{
  queryAddress: string
  network: Network.Network
}> {}

export type Deeplink =
  | InviteCodeJoin
  | Trade
  | UserProfile
  | Profile
  | Homepage
  | TelegramAuth
  | GoogleAuth
  | TwitterAuth
  | CheckCurrency
  | Nft

export const match = Data.matchFor<Deeplink>()

const DEEPLINK_OPTIONAL_PATHNAME_PREFIX = '/goto'
const DEEPLINK_PROFILE_PATHNAME = /^\/profile\/?$/i
const DEEPLINK_HOMEPAGE_PATHNAME = /^\/homepage\/?$/i
const DEEPLINK_NFT_PATHNAME = /^\/nft\/?$/i
const DEEPLINK_USERPROFILE_PATHNAME = /^\/profile\/(?<delugeId>.+)\/?$/i
const DEEPLINK_TRADE_PATHNAME = /^\/trade\/(?<network>.+)\/(?<pair>.+)\/?$/i
const DEEPLINK_CHECK_CURRENCY_PATHNAME = /^\/check-currency\/?$/i
const DEEPLINK_CHECK_CURRENCY_NETWORK_PARAM = 'network'
const DEEPLINK_CHECK_CURRENCY_QUERY_ADDRESS_PARAM = 'queryAddress'
const DEEPLINK_INVITE_JOIN_PATHNAME = /^\/invite\/?$/i
const DEEPLINK_TELEGRAM_AUTH_PATHNAME = /^\/tg-auth\/?$/i
const DEEPLINK_GOOGLE_AUTH_PATHNAME = /^\/google-auth\/?$/i
const DEEPLINK_TWITTER_AUTH_PATHNAME = /^\/twitter-auth\/?$/i
const DEEPLINK_INVITE_JOIN_PARAM = 'code'

function getUnifiedPathname(url: URL): string | undefined {
  if (url.protocol === `${environment.applinkScheme}:`) {
    const path = url.pathname.replace(/^\/+/, '/')
    return path.startsWith(DEEPLINK_OPTIONAL_PATHNAME_PREFIX)
      ? path.slice(DEEPLINK_OPTIONAL_PATHNAME_PREFIX.length)
      : path
  }
  if (!url.pathname.startsWith(DEEPLINK_OPTIONAL_PATHNAME_PREFIX)) {
    return undefined
  }
  return url.pathname.slice(DEEPLINK_OPTIONAL_PATHNAME_PREFIX.length)
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function fromURL(url: URL): Deeplink | undefined {
  const unifiedPathname = getUnifiedPathname(url)
  if (!unifiedPathname) {
    return undefined
  }

  if (unifiedPathname.match(DEEPLINK_PROFILE_PATHNAME)) {
    return new Profile()
  }
  if (unifiedPathname.match(DEEPLINK_HOMEPAGE_PATHNAME)) {
    return new Homepage()
  }
  if (unifiedPathname.match(DEEPLINK_NFT_PATHNAME)) {
    return new Nft()
  }
  const userProfileMatchResult = unifiedPathname.match(
    DEEPLINK_USERPROFILE_PATHNAME,
  )
  if (userProfileMatchResult) {
    const rawDelugeId = userProfileMatchResult.groups?.delugeId

    if (rawDelugeId) {
      return new UserProfile({
        delugeId: DelugeId(rawDelugeId),
      })
    }

    return
  }
  const tradeMatchResult = unifiedPathname.match(DEEPLINK_TRADE_PATHNAME)
  if (tradeMatchResult) {
    const pair = tradeMatchResult.groups?.pair
    const urlNetwork = tradeMatchResult.groups?.network

    if (pair && urlNetwork) {
      const network = Network.parse(urlNetwork.toLowerCase())
      if (network) {
        return new Trade({
          network,
          pair: PairAddress(pair),
        })
      }
    }

    return
  }
  const buyPremiumMatchResult = unifiedPathname.match(
    DEEPLINK_CHECK_CURRENCY_PATHNAME,
  )
  if (buyPremiumMatchResult) {
    const urlNetwork = url.searchParams.get(
      DEEPLINK_CHECK_CURRENCY_NETWORK_PARAM,
    )
    const urlQueryAddress = url.searchParams.get(
      DEEPLINK_CHECK_CURRENCY_QUERY_ADDRESS_PARAM,
    )

    if (urlQueryAddress && urlNetwork) {
      const network = Network.parse(urlNetwork.toLowerCase())

      if (network) {
        return new CheckCurrency({
          network,
          queryAddress: urlQueryAddress,
        })
      }
    }

    return
  }

  const referralMatchResult = unifiedPathname.match(
    DEEPLINK_INVITE_JOIN_PATHNAME,
  )
  if (referralMatchResult) {
    const inviteCode = url.searchParams.get(DEEPLINK_INVITE_JOIN_PARAM)
    if (inviteCode) {
      return new InviteCodeJoin({
        inviteCode: InviteCode(inviteCode),
      })
    }

    return
  }

  const telegramAuthMatchResult = unifiedPathname.match(
    DEEPLINK_TELEGRAM_AUTH_PATHNAME,
  )
  if (telegramAuthMatchResult) {
    const code = url.searchParams.get('code')
    const isExistsParam = url.searchParams.get('isExists')
    const isExists = isExistsParam
      ? BooleanUtil.parseString(isExistsParam)
      : undefined
    const action = url.searchParams.get('action')

    if (!code || isExists === undefined || !action) {
      return
    }

    return new TelegramAuth({
      code: OAuthCode(code),
      action,
      isExists,
    })
  }

  const googleAuthMatchResult = unifiedPathname.match(
    DEEPLINK_GOOGLE_AUTH_PATHNAME,
  )
  if (googleAuthMatchResult) {
    const stateParam = url.searchParams.get('state') || undefined
    const code = url.searchParams.get('code')

    if (!code) {
      return
    }

    return new GoogleAuth({
      state: stateParam ? OAuthState(stateParam) : undefined,
      code: OAuthCode(code),
    })
  }

  const twitterAuthMatchResult = unifiedPathname.match(
    DEEPLINK_TWITTER_AUTH_PATHNAME,
  )
  if (twitterAuthMatchResult) {
    const stateParam = url.searchParams.get('state') || undefined
    const authCode =
      url.searchParams.get('authCode') ?? url.searchParams.get('code')
    if (!authCode) {
      return
    }

    return new TwitterAuth({
      authCode: OAuthCode(authCode),
      state: stateParam ? OAuthState(stateParam) : undefined,
    })
  }
}
